import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import bgImg from "assets/images/dashboard_sub_bg.png";
import styles from "./svg.module.css";
import "./global.css";
import { useEnumApi } from "contexts/EnumApiContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { equipmentISOStatus, getEquipmentStatusColorByStatus, getEquipmentStatusColorByStatusOrCommunicationIssue, ROLE } from "data/data";
import useConfig from "hooks/useConfig";
import DashboardEquipmentDurationGraphs from "components/inner/chart/DashboardEquipmentDurationGraphs";
import { Button } from "@mui/material";

const INTERVAL_TIME = 3000; // 사용자 요청 속도 5000ms 반영
const ARROW_SPEED = 5000;

export default function CompanySvgViewer({
  fileId,
  zoneId,
  mapsId,
  zoneLocationMap,
  equipments,
  latestLogs,
}) {
  const isViewCommError = process.env.REACT_APP_SHOW_NETWORK_ERROR_TO_ALL_YN === 'Y';

  const { getEnumValueByEnumNameAndCode, role } = useEnumApi();
  const { t } = useTranslation();
  const { mode } = useConfig();
  const navigate = useNavigate();

  const stageRef = useRef(null);
  const svgObjRef = useRef(null);
  const overlayRef = useRef(null);
  const pathRef = useRef(null);
  const originRef = useRef(null);
  const arrowsRef = useRef(null);
  const svgDocRef = useRef(null);
  const labelGroupRef = useRef(null);

  const [equipmentList, setEquipmentList] = useState(null);
  const [originLabel, setOriginLabel] = useState("");
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [labelTitleColor, setLabelTitleColor] = useState({ fill: "#dddddd", stroke: "#000" });
  const [labelMode, setLabelMode] = useState("preview");
  const labelModeRef = useRef("preview");

  const equipmentListRef = useRef(null);
  const currentTargetRef = useRef(null);
  const prevActiveRef = useRef(null);
  const labelOffsetYRef = useRef(0);
  const animRAFRef = useRef(null);
  const basePhaseRef = useRef(0);
  const ALLOWED_IDS = useRef(new Set([])).current;

  const equipIdElRef = useRef(); const equipStatusRef = useRef(); const equipOpStatusRef = useRef(); const equipCommStatusRef = useRef();
  const equipmentCtgrRef = useRef(); const isoGradeRef = useRef(); const equipImgRef = useRef();
  const equipTempRef = useRef(); const equipHumRef = useRef(); const equipCo2Ref = useRef();

  const CARD_W = 270; 
  const CARD_H = 155; 
  const CARD_RX = 12; 
  // 모바일 툴팁 크기
  const MOBILE_CARD_W = 220;
  const MOBILE_CARD_H = 130;
  const MOBILE_CARD_RX = 10;

  const PRE_W = 270; const PRE_H = 40; const PRE_RX = 12;
  // const PRE_W = 250; const PRE_H = 85; const PRE_RX = 12;
  const TITLE_H = 26; const TITLE_RX = 8;
  const TITLE_TEXT_Y = -(CARD_H / 2) + 10 + TITLE_H / 2;
  const TABLE_Y = TITLE_TEXT_Y + TITLE_H / 2 + 12;
  const PRE_TITLE_H = 22;
  const PRE_TITLE_TEXT_Y = -(PRE_H / 2) + 8 + PRE_TITLE_H / 2;
  const PRE_TABLE_Y = PRE_TITLE_TEXT_Y + PRE_TITLE_H / 2 + 12;

  const [rollingIndex, setRollingIndex] = useState(-1);
  const rollingTimerRef = useRef(null);
  const [coordsMap, setCoordsMap] = useState({});
  const arrowColorRef = useRef("#2672f7"); // 초기값 파란색
  const rollingIndexRef = useRef(0);

  const SHOW_PREVIEW = false; // 👈 여기만 컨트롤
  const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

  // --- 핵심 기능 함수들 ---
  function applyPreviewOutline(el, meta) {
    if (!el || !meta) return;

    const colors = getEquipmentStatusColorByStatusOrCommunicationIssue(
      meta.status,
      ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue)
    );

    const color = colors?.bg2 || "#2672f7";

    el.setAttribute("stroke", color);
    el.setAttribute("stroke-width", "3");
    el.setAttribute("stroke-opacity", "0.9");
    el.style.filter = `drop-shadow(0px 0px 4px ${color})`;
  }

  const applyOutline = (el, isActive, statusColor = "#2672f7") => {
    if (!el) return;
    if (isActive) {
      // 설비 상태 색상을 stroke에 적용
      el.setAttribute("stroke", statusColor); 
      el.setAttribute("stroke-width", "7"); // 가독성을 위해 두께를 조금 더 키웠습니다.
      el.setAttribute("stroke-linejoin", "round");
      
      // 광채 효과도 상태 색상(statusColor)을 반영하도록 수정
      // 색상 코드를 rgba로 변환하기 번거로우므로, CSS 변수나 drop-shadow 색상만 변경
      // el.style.filter = `drop-shadow(0px 0px 10px ${statusColor}) brightness(1.3)`;
      el.style.filter = `
        drop-shadow(0px 0px 14px ${statusColor})
        drop-shadow(0px 0px 6px ${statusColor})
        brightness(1.35)
      `;
      el.style.transition = "all 0.3s ease";
    } else {
      // el.removeAttribute("stroke");
      // el.removeAttribute("stroke-width");
      // el.removeAttribute("stroke-linejoin");
      // el.style.filter = "none";
      // ✅ 변경: 프리뷰 상태로 복구
      const id = el.id;
      const meta = equipmentListRef.current?.[id];
      applyPreviewOutline(el, meta);
    }
  };

  function adjustMobileTooltipX() {
    if (!isMobile()) return;

    const overlay = overlayRef.current;
    const labelGroup = labelGroupRef.current;

    if (!overlay || !labelGroup) return;

    const overlayWidth = overlay.getBoundingClientRect().width;
    const margin = 8;
    const halfWidth = MOBILE_CARD_W / 2;

    // 설비 오브젝트의 현재 X 좌표
    const originTransform = originRef.current?.getAttribute("transform");
    const match = originTransform?.match(/translate\(\s*([-\d.]+)/);

    if (!match) return;

    const centerX = Number(match[1]);

    // 좌우 경계 보정
    let offsetX = 0;

    if (centerX - halfWidth < margin) {
      offsetX = margin + halfWidth - centerX;
    } else if (centerX + halfWidth > overlayWidth - margin) {
      offsetX = overlayWidth - margin - halfWidth - centerX;
    }

    // X축만 보정하고 Y축은 변경하지 않음
    labelGroup.setAttribute("transform", `translate(${offsetX}, 0)`);
  }

  const activateEquipment = (id, isAutoRolling = false) => {
    // console.log('[LOG 5] activateEquipment Executed for Target ID:', id, '| isAuto:', isAutoRolling, new Date().toLocaleTimeString());
    
    if (!svgDocRef.current || !equipmentListRef.current) {
      // console.log('[LOG 5-ERR] svgDocRef or equipmentListRef is null!');
      return;
    }
    
    const meta = equipmentListRef.current[id];
    const el = svgDocRef.current.getElementById(id);
    if (!el) {
      // console.log('[LOG 5-ERR] Element with ID:', id, 'not found in SVG Document!');
      return;
    }
    
    const colors = getEquipmentStatusColorByStatusOrCommunicationIssue(meta?.status, ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue));

    if (prevActiveRef.current) {
      applyOutline(prevActiveRef.current, false);
      prevActiveRef.current.classList.remove("active");
    }
    
    if (el) {
      el.classList.add("active");
      applyOutline(el, true, colors?.bg2 || "#2672f7"); 
      
      currentTargetRef.current = el;
      prevActiveRef.current = el;
      
      // 모드 및 Ref 업데이트
      labelModeRef.current = "full";
      setLabelMode("full");

      // [중요] 화면 밖(-1000)에 있던 카드를 현재 설비 위치로 소환
      // const center = getSvgElementCenter(el);
      // if (originRef.current && center.x !== 0) {
      //   originRef.current.setAttribute("transform", `translate(${center.x},${center.y})`);
      // }
      const center = getSvgElementCenter(el);

      // if (originRef.current && center.x !== 0) {
      //   if (isMobile()) {
      //     // 모바일에서만 툴팁 위치 조정
      //     const offsetX = 0;
      //     const offsetY = center.y < 100 ? 45 : -90;

      //     originRef.current.setAttribute(
      //       "transform",
      //       `translate(${center.x + offsetX},${center.y + offsetY})`
      //     );

      //     labelGroupRef.current?.setAttribute(
      //       "transform",
      //       "translate(0,0)"
      //     );
      //   } else {
      //     // PC 기존 위치 유지
      //     originRef.current.setAttribute(
      //       "transform",
      //       `translate(${center.x},${center.y})`
      //     );
      //   }
      // }

      // updateCard(id);
      if (originRef.current && center.x !== 0) {
        originRef.current.setAttribute("transform", `translate(${center.x},${center.y})`);
      }

      updateCard(id);

      if (isMobile()) {
        requestAnimationFrame(() => {
          adjustMobileTooltipX();
        });
      }

      // 자동 롤링이 아닐 때(클릭일 때)만 롤링 타이머를 멈춤
      if (!isAutoRolling) {
        stopAutoRolling();
      }
    }
    // console.log('[LOG 5-SUCCESS] Equipment Successfully Activated:', id);
  };

  const startAutoRolling = (isImmediate = false) => {
    if (rollingTimerRef.current) clearInterval(rollingTimerRef.current);
    
    const allKeys = Object.keys(equipmentListRef.current || {});
    const keys = allKeys.filter(id => ALLOWED_IDS.has(id));

    // console.log('[LOG 4] startAutoRolling Called | isImmediate:', isImmediate, '| Filtered Keys:', keys, new Date().toLocaleTimeString());
    // const keys = Object.keys(equipmentListRef.current || {});
    if (keys.length === 0) {
      // console.log('[LOG 4-WARN] Filtered keys is EMPTY! (ALLOWED_IDS or equipmentListRef missing)');
      return;
    }

    // 인덱스 범위를 유효한 keys 길이 내로 안전하게 맞춤
    if (rollingIndexRef.current >= keys.length) {
      rollingIndexRef.current = 0;
    }

    // 인자가 true일 때만 즉시 실행 (페이지 첫 로드 시에 사용)
    if (isImmediate) {
      // console.log('[LOG 4-1] Immediate Activation For Index 0:', keys[rollingIndexRef.current]);
      activateEquipment(keys[rollingIndexRef.current], true);
    }

    rollingTimerRef.current = setInterval(() => {
      // 다음 인덱스 계산
      const nextIndex = (rollingIndexRef.current + 1) % keys.length;
      rollingIndexRef.current = nextIndex;
      setRollingIndex(nextIndex);

      // 자동 롤링임을 알리는 true 파라미터 전달
      // console.log('[LOG 4-2] Interval Rolling Step -> Target:', keys[nextIndex], new Date().toLocaleTimeString());
      activateEquipment(keys[nextIndex], true);
    }, INTERVAL_TIME); // 5000ms
  };

  const stopAutoRolling = () => {
    if (rollingTimerRef.current) {
      clearInterval(rollingTimerRef.current);
      rollingTimerRef.current = null;
    }
  };

  const extractAllCoordinates = (doc) => {
    const newCoords = {};
    ALLOWED_IDS.forEach(id => {
      const el = doc.getElementById(id);
      if (el) {
        const center = getSvgElementCenter(el);
        if (center.x !== 0) newCoords[id] = center;
      }
    });
    setCoordsMap(newCoords);
  };

  // --- 데이터 처리 및 Card 업데이트 로직 ---

  useEffect(() => {
    if (equipments) {
      const LOG_GROUP = latestLogs?.reduce((acc, log) => {
        acc[log.equipmentId] ??= {};
        acc[log.equipmentId][log.logCode] = log;
        return acc;
      }, {}) || {};

      const EQUIP_DB = Object.fromEntries(
        equipments.map((item) => {
          const tLog = (process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' ? LOG_GROUP[item.id]?.T : null);
          const hLog = LOG_GROUP[item.id]?.RH;
          const cLog = LOG_GROUP[item.id]?.CO2;
          return [item.equipmentNo, {
            ...item,
            statusTitle: getEnumValueByEnumNameAndCode("EquipmentFaultStatus", item.status)?.[0]?.title || "-",
            isoGrade: equipmentISOStatus.find((iso) => iso.code === item.lowestDiagnosisRank) || { code: "-" },
            equipOpStatusTitle: item.latestEquipmentOp?.opStatus === "ACTIVE" ? t('message.common.text.active') : t('message.common.text.stop'),
            equipCommTitle: item.hasCommunicationIssue ? t('message.common.text.issue') : t('message.common.normal'),
            temperature: tLog ? `${tLog.logValue}${tLog.logUnit ?? ""}` : "-",
            humidity: hLog ? `${hLog.logValue}${hLog.logUnit ?? ""}` : "-",
            co2: cLog ? `${cLog.logValue}${cLog.logUnit ?? ""}` : "-",
          }];
        })
      );
      // console.log('[LOG 1] EQUIP_DB Created | Count:', Object.keys(EQUIP_DB).length, new Date().toLocaleTimeString());
      setEquipmentList(EQUIP_DB);
      equipmentListRef.current = EQUIP_DB;
      ALLOWED_IDS.clear();
      Object.keys(EQUIP_DB).forEach(id => ALLOWED_IDS.add(id));
    }
  }, [equipments, latestLogs, t]);

  const getValueStyle = (type, meta) => {
    if (!meta) return { backgroundColor: '#d6d6d6', color: '#000' };
    if (type === 'status') {
      const colors = getEquipmentStatusColorByStatus(meta.status);
      return { backgroundColor: colors?.bg2 || '#dddddd', color: colors.color };
    }
    if (type === 'op') {
      return { backgroundColor: meta.latestEquipmentOp?.opStatus?.toUpperCase() === "ACTIVE" ? "#2672f7" : "#777", color: '#fff' };
    }
    if (type === 'comm') {
      return { backgroundColor: ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue) ? "#777" : "#548235", color: '#fff' };
    }
    if (type === 'iso') {
      const isoData = equipmentISOStatus.find((iso) => iso.code === meta.lowestDiagnosisRank);
      return { backgroundColor: isoData?.color3 || '#62b929', color: meta.lowestDiagnosisRank === "C" ? "#000" :"#fff" };
    }
    return { backgroundColor: '#d6d6d6', color: '#000' };
  };

  function resetCard() {
    // 1. 모든 강조 및 선택 상태 초기화
    if (currentTargetRef.current) {
      applyOutline(currentTargetRef.current, false);
      currentTargetRef.current.classList.remove("active");
    }
    currentTargetRef.current = null;
    prevActiveRef.current = null;
    setSelectedEquipmentId(null); 
    setOriginLabel("");

    // 2. 모드 리셋 및 카드 숨기기
    setLabelMode("preview");
    labelModeRef.current = "preview";
    if (originRef.current) {
      originRef.current.setAttribute("transform", "translate(-1000,-1000)");
    }
    if (pathRef.current) pathRef.current.setAttribute("d", "");
    if (arrowsRef.current) {
      arrowsRef.current.innerHTML = ""; // 화살표 잔상 즉시 제거
    }
    // 화살표 색상 Ref도 초기화 (기본색이 있다면 설정)
    arrowColorRef.current = "#2672f7";

    // 3. [핵심] 사이드바(오른쪽 영역) 초기화 추가
    if (equipIdElRef.current) {
      equipIdElRef.current.textContent = t("message.common.chip.selection.wait");
      equipIdElRef.current.style.backgroundColor = ""; // 배경색 제거
      equipIdElRef.current.style.color = "";           // 글자색 리셋
    }
    if (equipImgRef.current) {
      equipImgRef.current.src = `${process.env.PUBLIC_URL}/img/sample.png`; // 기본 이미지로
    }
    if (equipmentCtgrRef.current) equipmentCtgrRef.current.textContent = "-";
    if (equipStatusRef.current) {
      equipStatusRef.current.textContent = "-";
      equipStatusRef.current.style.backgroundColor = "";
    }
    if (equipOpStatusRef.current) {
      equipOpStatusRef.current.textContent = "-";
      equipOpStatusRef.current.style.backgroundColor = "";
      equipOpStatusRef.current.style.color = "";
    }
    if (equipCommStatusRef.current) {
      equipCommStatusRef.current.textContent = "-";
      equipCommStatusRef.current.style.backgroundColor = "";
    }
    if (isoGradeRef.current) {
      isoGradeRef.current.textContent = "-";
      isoGradeRef.current.style.backgroundColor = "";
      isoGradeRef.current.style.color = "";
    }
    // 온도, 습도, CO2 초기화
    if (equipTempRef.current) { equipTempRef.current.textContent = "-"; equipTempRef.current.style.display = ""; }
    if (equipHumRef.current) { equipHumRef.current.textContent = "-"; equipHumRef.current.style.display = ""; }
    if (equipCo2Ref.current) { equipCo2Ref.current.textContent = "-"; equipCo2Ref.current.style.display = ""; }

    // 화살표 색상 변수 초기화
    document.documentElement.style.setProperty("--arrow", "transparent");

    // 4. 롤링 재개
    startAutoRolling(false);
  }

  function updateCard(targetId) {
    const meta = equipmentListRef.current[targetId];
    if (!meta) return;
    setSelectedEquipmentId(meta.id);
    setOriginLabel(meta.name);
    const colors = getEquipmentStatusColorByStatusOrCommunicationIssue(meta.status, ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue));
    
    // Ref에 현재 상태 색상을 즉시 저장 (React 렌더링 사이클 무관)
    arrowColorRef.current = colors?.bg2 || "#2672f7";
    
    setLabelTitleColor({ fill: colors?.bg2, stroke: colors?.stroke });

    if (equipIdElRef.current) {
      equipIdElRef.current.textContent = `${meta.name}`;
      equipIdElRef.current.style.backgroundColor = colors?.bg2;
      equipIdElRef.current.style.color = colors?.color;
    }
    if (equipmentCtgrRef.current) equipmentCtgrRef.current.textContent = `${t("message.common.subtitle.equipment.level3")}: ${meta.equipmentCtgr?.name}` || "-";
    if (equipStatusRef.current) {
      const style = getValueStyle('status', meta);
      equipStatusRef.current.textContent = `${t("message.common.subtitle.status")}: ${meta.statusTitle}`;
      equipStatusRef.current.style.backgroundColor = style.backgroundColor;
    }
    if (equipOpStatusRef.current) {
      const style = getValueStyle('op', meta);
      equipOpStatusRef.current.textContent = `${t("message.common.subtitle.operation.info")}: ${meta.equipOpStatusTitle}`;
      equipOpStatusRef.current.style.backgroundColor = style.backgroundColor;
      equipOpStatusRef.current.style.color = style.color;
    }
    if (equipCommStatusRef.current) {
      const style = getValueStyle('comm', meta);
      equipCommStatusRef.current.textContent = `${t("message.common.subtitle.communication")}: ${meta.equipCommTitle}`;
      equipCommStatusRef.current.style.backgroundColor = style.backgroundColor;
    }
    if (isoGradeRef.current) {
      const style = getValueStyle('iso', meta);
      isoGradeRef.current.textContent = `${t('message.common.subtitle.iso')}${t("message.common.subtitle.grade")}: ${meta.isoGrade?.code || "-"}`;
      isoGradeRef.current.style.backgroundColor = style.backgroundColor;
      isoGradeRef.current.style.color = meta.isoGrade?.code === "C" ? "#000" :"#fff"
    }
    if (equipTempRef.current) { equipTempRef.current.style.display = meta.temperature === "-" ? "none" : ""; equipTempRef.current.textContent = meta.temperature; }
    if (equipHumRef.current) { equipHumRef.current.style.display = meta.humidity === "-" ? "none" : ""; equipHumRef.current.textContent = meta.humidity; }
    if (equipCo2Ref.current) { equipCo2Ref.current.style.display = meta.co2 === "-" ? "none" : ""; equipCo2Ref.current.textContent = meta.co2; }
    if (equipImgRef.current) {
      equipImgRef.current.src = meta.imgFiles?.length > 0
        ? `${window.RUNTIME_CONFIG.REACT_APP_API_URL}/api/download/${meta.imgFiles[0].id}`
        : `${process.env.PUBLIC_URL}/img/sample.png`;
    }
    document.documentElement.style.setProperty("--arrow", colors?.bg2);
  }


  // --- 경로 및 애니메이션 로직 ---
  function buildArrowsAlongPath() {
    if (!pathRef.current || !arrowsRef.current) return;

    // 모바일에서는 화살표를 표시하지 않음
    if (isMobile()) {
      arrowsRef.current.innerHTML = "";
      return;
    }
    
    const length = pathRef.current.getTotalLength();
    if (length === 0) {
      arrowsRef.current.innerHTML = ""; // 길이가 없으면 비움
      return;
    }

    // Ref에서 현재 색상을 직접 가져옴 (가장 빠르고 확실함)
    const activeColor = arrowColorRef.current;
    const step = 30;
    let html = "";

    for (let i = 0; i < length; i += step) {
      const p = (i / length + basePhaseRef.current) % 1;
      const pt = pathRef.current.getPointAtLength(p * length);
      const pt2 = pathRef.current.getPointAtLength(Math.min(p * length + 1, length));
      const angle = Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * 180 / Math.PI;
      
      // !important를 사용하여 스타일 충돌 방지 및 개별 경로 생성
      html += `
        <path 
          d="M-6,-4 L0,0 L-6,4 Z" 
          fill="${activeColor}" 
          stroke="${activeColor}"
          style="fill: ${activeColor} !important; stroke: ${activeColor} !important;" 
          transform="translate(${pt.x},${pt.y}) rotate(${angle})" 
        />`;
    }

    // 기존 내용을 완전히 덮어씌움
    arrowsRef.current.innerHTML = html;
  }

  function redrawConnectorAndArrows() {
    // 모바일에서는 연결선과 화살표 모두 제거
    if (isMobile()) {
      if (pathRef.current) {
        pathRef.current.setAttribute("d", "");
      }

      if (arrowsRef.current) {
        arrowsRef.current.innerHTML = "";
      }

      return;
    }

    if (!currentTargetRef.current || labelModeRef.current !== "full") {
      if (pathRef.current) pathRef.current.setAttribute("d", "");
      if (arrowsRef.current) arrowsRef.current.innerHTML = "";
      return;
    }
    const center = getSvgElementCenter(currentTargetRef.current);
    const header = document.getElementById("cardHeader");
    if (!header || center.x === 0) return;

    const overlayRect = overlayRef.current.getBoundingClientRect();
    const headRect = header.getBoundingClientRect();

    // -------------------------------------------------------------
    // 💡 App.js의 scale로 인한 좌표 왜곡 보정
    // .monitering에 적용된 scale 값을 읽어옵니다. (없으면 1)
    let currentScale = 1;
    const monitoringEl = document.querySelector('.monitering');
    if (monitoringEl) {
      const transform = window.getComputedStyle(monitoringEl).transform;
      if (transform && transform !== 'none') {
        const matrix = transform.match(/^matrix\((.+)\)$/);
        if (matrix) {
          currentScale = parseFloat(matrix[1].split(',')[0]) || 1;
        }
      }
    }

    // getBoundingClientRect()는 스케일이 적용된 '실제 브라우저 픽셀' 기준이므로,
    // SVG 내부 1920px 좌표계로 맞추기 위해 currentScale로 나누어 줍니다.
    const end = { 
      x: (headRect.left - overlayRect.left) / currentScale + 8, 
      y: (headRect.top - overlayRect.top) / currentScale + headRect.height / (2 * currentScale) 
    };
    // -------------------------------------------------------------

    originRef.current?.setAttribute("transform", `translate(${center.x},${center.y})`);
    let offsetY = center.y < 70 ? 45 : -90;  // 카드 위치 수정
    labelOffsetYRef.current = offsetY;
    labelGroupRef.current?.setAttribute("transform", `translate(0,${offsetY})`);
    const start = { x: center.x, y: center.y + labelOffsetYRef.current - CARD_H / 2 };
    const up = end.y < start.y ? -1 : 1; const right = end.x >= start.x ? 1 : -1; const r = 26;
    pathRef.current?.setAttribute("d", `M ${start.x},${start.y} L ${start.x},${end.y - up * r} Q ${start.x},${end.y} ${start.x + right * r},${end.y} L ${end.x},${end.y}`);
    buildArrowsAlongPath();
  }

  function getSvgElementCenter(el) {
    if (!el || !svgObjRef.current || !overlayRef.current) return { x: 0, y: 0 };
    const objRect = svgObjRef.current.getBoundingClientRect();
    const innerRect = el.getBoundingClientRect();
    const overlayRect = overlayRef.current.getBoundingClientRect();
    return {
      x: objRect.left + innerRect.left - overlayRect.left + innerRect.width / 2,
      y: objRect.top + innerRect.top - overlayRect.top + innerRect.height / 2,
    };
  }

  useEffect(() => {
    const step = (timestamp) => {
      basePhaseRef.current = (timestamp / ARROW_SPEED) % 1;
      if (labelModeRef.current === "full") redrawConnectorAndArrows();
      animRAFRef.current = requestAnimationFrame(step);
    };
    animRAFRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRAFRef.current);
  }, []);

  useEffect(() => {
    if (!fileId) return;
    // console.log('[LOG 2] SVG Download Started | fileId:', fileId, new Date().toLocaleTimeString());
    axios.get(`${window.RUNTIME_CONFIG.REACT_APP_API_URL}/api/download/${fileId}`, { responseType: "blob" })
      .then(res => {
        // console.log('[LOG 2-1] SVG Download Finished', new Date().toLocaleTimeString());
        const url = URL.createObjectURL(res.data);
        if (svgObjRef.current) svgObjRef.current.data = url;
      });
  }, [fileId]);

  // SVG 로드 및 이벤트 리스너
  useEffect(() => {
    const svgObj = svgObjRef.current;
    if (!svgObj || !equipmentList) return;

    const onLoad = () => {
      // console.log('[LOG 3] SVG Object onLoad Event Fired', new Date().toLocaleTimeString());
      const doc = svgObj.contentDocument;
      if (!doc) {
        // console.log('[LOG 3-ERR] contentDocument is null!');
        return;
      }
      svgDocRef.current = doc;

      // 💡 SVG 내부 요소가 실제로 생성되었는지 100ms마다 체크하는 재귀 함수
      const waitForSvgElements = (retryCount = 0) => {
        // equipmentList의 실제 설비 ID가 SVG 내부에 생성되었는지 확인
        // SVG의 모든 [id] 요소를 설비로 취급하지 않고, ALLOWED_IDS만 설비 대상으로 사용
        const foundElements = Array.from(ALLOWED_IDS)
          .map(id => doc.getElementById(id))
          .filter(Boolean);

        if (foundElements.length > 0) {
          // console.log(`[LOG 3-SUCCESS] SVG Elements Ready! Found: ${foundElements.length} (Retries: ${retryCount})`, new Date().toLocaleTimeString());

          // 1. ALLOWED_IDS 재동기화 (실제 SVG 안에 존재하는 요소들 기준)
          ALLOWED_IDS.clear();
          foundElements.forEach(el => {
            ALLOWED_IDS.add(el.id);
            el.classList.add("cmms_equip");
          });

          // 2. 좌표 추출
          extractAllCoordinates(doc);

          // 3. 자동 롤링 시작 (최초 1회만 호출)
          // console.log('[LOG 3-1] Triggering Initial startAutoRolling(true)', new Date().toLocaleTimeString());
          startAutoRolling(true);

          // 4. 이벤트 및 스타일 바인딩 (DOM이 완벽히 그려진 후 적용)
          attachSvgEventsAndStyles(doc);

        } else {
          if (retryCount > 150) { // 최대 15초 동안 대기
            // console.log('[LOG 3-WARN] SVG elements loading timeout.');
            return;
          }
          // 아직 SVG 내부 DOM 생성이 안 끝났다면 100ms 후 다시 확인
          setTimeout(() => waitForSvgElements(retryCount + 1), 100);
        }
      };

      // 대기 로직 실행
      waitForSvgElements();
    };

    // 💡 이벤트 등록 및 스타일 부여 로직을 분리한 함수
    const attachSvgEventsAndStyles = (doc) => {
      // [클릭 이벤트 리스너]
      doc.addEventListener("click", (e) => {
        const el = e.target.closest(".cmms_equip");
        const keys = Object.keys(equipmentListRef.current || {}).filter(id => ALLOWED_IDS.has(id));

        if (el && ALLOWED_IDS.has(el.id)) {
          if (currentTargetRef.current === el) {
            startAutoRolling(false);
            return;
          }

          const clickedIndex = keys.indexOf(el.id);
          if (clickedIndex !== -1) {
            rollingIndexRef.current = clickedIndex;
            setRollingIndex(clickedIndex);
          }
          
          stopAutoRolling();
          activateEquipment(el.id, false);

        } else {
          startAutoRolling(false);
          return;
        }
      });

      // [기초 스타일 및 호버 효과]
      ALLOWED_IDS.forEach(id => {
        const el = doc.getElementById(id);
        const meta = equipmentListRef.current?.[id];

        if (el && meta) {
          el.classList.add("cmms_equip");
          el.style.cursor = "pointer";
          el.style.transition = "all 0.2s ease";

          const colors = getEquipmentStatusColorByStatusOrCommunicationIssue(
            meta.status,
            ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue)
          );

          const color = colors?.bg2 || "#2672f7";
          
          el.setAttribute("stroke", color);
          el.setAttribute("stroke-width", "3");
          el.setAttribute("stroke-opacity", "0.9");
          el.style.filter = `drop-shadow(0px 0px 4px ${color})`;

          el.addEventListener("mouseenter", () => {
            if (!el.classList.contains("active")) {
              el.setAttribute("stroke-width", "4");
            }
          });
          el.addEventListener("mouseleave", () => {
            if (!el.classList.contains("active")) {
              el.setAttribute("stroke-width", "3");
            }
          });
        }
      });
    };

    svgObj.addEventListener("load", onLoad);
    return () => svgObj.removeEventListener("load", onLoad);
  }, [equipmentList]);

  return (
    <div className={styles.app} data-theme={mode === "light" ? "white" : "dark"}>
      <style>{`
        /* 선택된 설비 아웃라인 강조 효과 (cmms_equip 기준) */
        .cmms_equip.active {
          stroke: var(--equip-color, #2672f7) !important;
          stroke-width: 6px !important;
          stroke-dasharray: none !important;
          opacity: 1 !important;
          filter: drop-shadow(0px 0px 14px var(--equip-color)) brightness(1.3);
        }
        /* ✅ 프리뷰 (항상 표시되는 약한 활성) */
        .cmms_equip.has-data:not(.active) {
          stroke: var(--equip-color);
          stroke-width: 3px;

          /* 🔥 핵심 */
          stroke-opacity: 0.9;

          /* 🔥 살짝 glow */
          filter: drop-shadow(0px 0px 4px var(--equip-color));
        }
        #connectorPath { stroke: var(--arrow, #33b5ff); stroke-width: 3px; fill: none; opacity: 0.6; stroke-dasharray: 5,5; }
        .isOff { display: none; } .isOn { display: inline; }
      `}</style>
      
      <div className={styles.stage} ref={stageRef} style={{ backgroundImage: `url(${bgImg})`, backgroundSize: "cover" }}>
        <object ref={svgObjRef} className={styles.svg_host} type="image/svg+xml" />
        <svg className={styles.overlay} ref={overlayRef} style={{ height: "400px", overflow: "visible", pointerEvents: "none" }}>
          <defs>
            <linearGradient id="cardBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(160, 91, 159, 0.65)" /><stop offset="50%" stopColor="rgba(0, 156, 200, 0.45)" /><stop offset="100%" stopColor="rgba(98, 224, 79, 0.35)" />
            </linearGradient>
          </defs>
          <path id="connectorPath" ref={pathRef} />
          <g ref={arrowsRef} />

          {equipmentList && Object.entries(equipmentList).map(([svgId, meta]) => {
            const center = coordsMap[svgId];
            if (!SHOW_PREVIEW || !center || selectedEquipmentId === meta.id) return null;
            const colors = getEquipmentStatusColorByStatusOrCommunicationIssue(meta.status, ((isViewCommError || role === ROLE.SA.code) && meta?.hasCommunicationIssue));

            // ✅ 프리뷰 카드에서만 투명도 연하게 (배경/타이틀만)
            const PRE_BG_OPACITY = 0.55;     // 그라데이션 배경
            const PRE_TITLE_OPACITY = 0.70;  // 타이틀 바탕

            return (
              <g key={svgId} transform={`translate(${center.x}, ${center.y - 25})`}>
              {/* <g key={svgId} transform={`translate(${center.x}, ${center.y - 65})`}> */}
                {/* ✅ (수정) 프리뷰 그라데이션 배경 투명도 */}
                <rect
                  x={-PRE_W/2}
                  y={-PRE_H/2}
                  width={PRE_W}
                  height={PRE_H}
                  rx={PRE_RX}
                  style={{
                    fill: "url(#cardBgGradient)",
                    stroke: "rgba(255,255,255,0.2)",
                    opacity: PRE_BG_OPACITY,
                  }}
                />

                {/* ✅ (수정) 타이틀 파란 바탕 투명도 */}
                <rect
                  x={-PRE_W/2+10}
                  y={-PRE_H/2+8}
                  width={PRE_W-20}
                  height={PRE_TITLE_H}
                  rx={10}
                  style={{
                    fill: colors?.bg,
                    stroke: colors?.stroke,
                    opacity: PRE_TITLE_OPACITY,
                  }}
                />

                <text x="0" y={PRE_TITLE_TEXT_Y} textAnchor="middle" dominantBaseline="middle" style={{ fill: "#fff", fontSize: 13, fontWeight: 800 }}>
                  {meta.name}
                </text>
               
              </g>
            );
          })}

          <g ref={originRef} transform="translate(-1000,-1000)">
            <g ref={labelGroupRef}>
              <g 
                className={`fullLabel ${labelMode === "full" ? "isOn" : "isOff"}`}
                transform={isMobile() ? "translate(0, -70)" : undefined}
              >
                <rect 
                  x={-(isMobile() ? MOBILE_CARD_W : CARD_W) / 2}
                  y={-(isMobile() ? MOBILE_CARD_H : CARD_H) / 2}
                  width={isMobile() ? MOBILE_CARD_W : CARD_W}
                  height={isMobile() ? MOBILE_CARD_H : CARD_H}
                  rx={isMobile() ? MOBILE_CARD_RX : CARD_RX}
                  style={{ fill: "url(#cardBgGradient)", stroke: "rgba(255,255,255,0.3)", strokeWidth: 2 }} 
                />
                {/* <rect x={-CARD_W/2} y={-CARD_H/2} width={CARD_W} height={CARD_H} rx={CARD_RX} style={{ fill: "url(#cardBgGradient)", stroke: "rgba(255,255,255,0.3)", strokeWidth: 2 }} /> */}
                <g transform={isMobile() ? "scale(0.815)" : undefined}>
                  <rect x={-CARD_W/2+10} y={-CARD_H/2+10} width={CARD_W-20} height={TITLE_H} rx={TITLE_RX} style={{ fill: labelTitleColor.fill, stroke: labelTitleColor.stroke }} />
                  <text x="0" y={TITLE_TEXT_Y} textAnchor="middle" dominantBaseline="middle" style={{ fill: "#fff", fontSize: 13, fontWeight: 900 }}>{originLabel}</text>
                  <foreignObject x={-CARD_W/2 + 25} y={TABLE_Y - 8} width={CARD_W - 50} height={120}>
                    <div xmlns="http://www.w3.org/1999/xhtml" style={{ color: '#fff', fontSize: '12px', fontWeight: 700, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: '50px', opacity: 0.8 }}>{t("message.common.subtitle.equipment.level3")}</span>
                        <span style={{ marginLeft: '10px' }}>{equipmentListRef.current?.[currentTargetRef.current?.id]?.equipmentCtgr?.name || "-"}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: '50px', opacity: 0.8 }}>{t("message.common.subtitle.status")}</span>
                        <span style={{ ...getValueStyle('status', equipmentListRef.current?.[currentTargetRef.current?.id])
                          , marginLeft: '10px', padding: '1px 10px', borderRadius: '12px' }}
                        >
                          {equipmentListRef.current?.[currentTargetRef.current?.id]?.statusTitle || "-"}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: '50px', opacity: 0.8 }}>{t("message.common.subtitle.operation.info")}</span>
                        <span style={{ 
                          ...getValueStyle('op', equipmentListRef.current?.[currentTargetRef.current?.id])
                          , marginLeft: '10px', padding: '1px 10px', borderRadius: '12px' }}
                        >
                          {equipmentListRef.current?.[currentTargetRef.current?.id]?.equipOpStatusTitle || "-"}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ width: '50px', opacity: 0.8 }}>{t('message.common.subtitle.iso')} {t("message.common.subtitle.grade")}</span>
                        <span style={{ ...getValueStyle('iso', equipmentListRef.current?.[currentTargetRef.current?.id])
                          , marginLeft: '10px', padding: '1px 10px', borderRadius: '12px' }}
                        >
                          {equipmentListRef.current?.[currentTargetRef.current?.id]?.isoGrade?.code || "-"}
                        </span>
                      </div>
                    </div>
                  </foreignObject>
                </g>
              </g>
            </g>
          </g>
        </svg>

        {zoneLocationMap?.length > 1 && (
          <div style={{ position: "absolute", bottom: "10px", left: "10px", right: "10px" }}>
            {zoneLocationMap?.map((map, bi) => (
              <Button key={map.id + "_a"} variant={map.id == mapsId ? "contained" : "outlined"} color="primary" style={{ margin: "5px" }} onClick={() => navigate(`/dashboard/sub?zId=${zoneId}&mId=${map.id}`)}>
                {t("message.common.subtitle.equipmentPart.image")} {bi + 1}
              </Button>
            ))}
          </div>
        )}
      </div>

      <aside className={styles.card} data-theme={mode === "light" ? "white" : "dark"} id="infoCard">
        <div className={styles.row} id="cardHeader" style={{ cursor: selectedEquipmentId ? "pointer" : "default" }} onClick={() => selectedEquipmentId && navigate(`/equipment/${selectedEquipmentId}`)}>
          <span className={styles.pill} ref={equipIdElRef} style={{ fontWeight: "bold" }}>{t("message.common.chip.selection.wait")}</span>
        </div>
        <img ref={equipImgRef} src={`${process.env.PUBLIC_URL}/img/sample.png`} className={`${styles.thumb} ${styles.ghost}`} alt="img" style={{ width: "100%", cursor: selectedEquipmentId ? "pointer" : "default" }} onClick={() => selectedEquipmentId && navigate(`/equipment/${selectedEquipmentId}`)} />
        <div><div className={styles.meta}>{t("message.common.text.select.equipment.realtime.info.display")}</div></div>
        <div className={styles.row} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <div className={styles.pill} ref={equipmentCtgrRef}>-</div>
          <div className={styles.pill} ref={equipStatusRef} style={{ color: '#fff' }}>-</div>
          <div className={styles.pill} ref={equipOpStatusRef} /*style={{ color: '#fff' }}*/>-</div>
          {(role === ROLE.SA.code) && <div className={styles.pill} ref={equipCommStatusRef} style={{ color: '#fff' }}>-</div>}
          <div className={styles.pill} ref={isoGradeRef} /*style={{ color: '#fff' }}*/>-</div>
          <div className={styles.pill} ref={equipTempRef}>-</div>
          <div className={styles.pill} ref={equipHumRef}>-</div>
          <div className={styles.pill} ref={equipCo2Ref}>-</div>
        </div>
        {selectedEquipmentId && <DashboardEquipmentDurationGraphs equipmentId={selectedEquipmentId} height={145} />}
      </aside>
    </div>
  );
}